import React, { FC, useEffect, useState } from 'react'
import { Spin } from 'antd'
import cx from 'classnames'
import { v4 as uuidv4 } from 'uuid'
import { get, omit, set, filter, partition, isEmpty, forEach } from 'lodash'
import { useDispatch } from 'react-redux'
import { initialize } from 'redux-form'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import Hotkeys from 'react-hot-keys'

// components
import ItemListLeftSideBar from './components/ItemListLeftSideBar'
import MainBuilder from './components/MainBuilder'
import elementsList from './components/elementsList'
import ItemPropertiesRightSideBar from './components/ItemPropertiesRightSideBar'

// reducers
import { FORM_EDITED, LOAD, RESET_FORM_STORE } from '../../reducers/form/formTypes'
import {
	getColumns,
	getElements,
	getEmailsConfigs,
	getFormTableItems,
	getFormTabs,
	getFormTemplate,
	getProcedures,
	saveItemCopy
} from '../../reducers/form/formActions'
import { getTemplate } from '../../reducers/templates/templateActions'

// types
import {
	IElementItem,
	IFormElement,
	IFormItemData,
	IGridItem,
	IListItem,
	IParsedData,
	ITabBoxLayoutRef,
	ITabOption,
	ITabsElement
} from '../../types/interfaces'

// utils
import { deleteReq, patchReq, postReq } from '../../utils/request'
import {
	DEFAULT_LABEL_COLOR,
	DEFAULT_HINT_COLOR,
	DEFAULT_PLACEHOLDER_COLOR,
	DEFAULT_NUMBERS_OF_COLS,
	DEFAULT_TITLE_SIZE,
	DROPPING_ELEMENT,
	ELEMENT_ATTRIBUTES,
	ELEMENT_TYPES,
	FIELD_SIZE,
	FORM,
	ITEM_PROPERTIES_RIGHT_SIDEBAR_TAB_KEYS,
	ITEM_LABEL_POSITION,
	LAYOUT_ROW_HEIGHT,
	NUMBER_OF_ROWS,
	DEFAULT_DISPLAYED_OPTIONS,
	RADIO_BUTTON_GROUP_ALIGNMENT,
	DEFAULT_TAB_BOX_HEIGHT,
	TAB_BOX_TABS_NAV_WRAP_HEIGHT,
	PAGE,
	FORM_KEYS,
	VALIDATION_FORM_FIELD_ARRAYS,
	BUTTON_ACTIONS,
	BUTTON_ACTIONS_ATTRIBUTES,
	KEYS_SHORTCUTS,
	OBJECTS,
	STATUS_OBJECT_DEFAULT_VALUES,
	ELEMENTS_WITH_VISIBILITY,
	ELEMENTS_WITH_VISIBILITY_AND_VALIDATION
} from '../../utils/enums'
import { addEmptyRow, attributesStartingWith, getApiEndpointPath } from '../../utils/helper'

type Props = {
	page: PAGE
}

const FormBuilderPage: FC<Props> = (props) => {
	const [t] = useTranslation()
	const { templateId } = useParams<{ templateId?: string }>()
	const { page } = props
	const dispatch = useDispatch()
	const [elements, setElements] = useState<IListItem[]>([...elementsList])
	const [layout, setLayout] = useState<IGridItem[]>([])
	const [items, setItems] = useState<IElementItem>({})
	const [selectedItemId, setSelectedItemIndex] = useState<string>('')
	const [selectedType, setSelectedType] = useState<string>('')
	const [selectedItem, setSelectedItem] = useState<string>('')
	const [selectedTab, setSelectedTab] = useState<string>('')
	const [selectedTabItem, setSelectedTabItem] = useState<string>('')
	const [selectedNestedTab, setSelectedNestedTab] = useState<string>('')
	const [selectedNestedTabItem, setSelectedNestedTabItem] = useState<string>('')
	const [tabBoxLayoutReferences, setTabBoxLayoutReferences] = useState<ITabBoxLayoutRef>({})
	const [activeElPropertiesTabKey, setActiveElPropertiesTabKey] = useState<string>(ITEM_PROPERTIES_RIGHT_SIDEBAR_TAB_KEYS.TAB3)
	const [database, setDatabase] = useState<string>('')
	const [spinner, setSpinner] = useState<boolean>(false)
	const buttons = attributesStartingWith(items, `${ELEMENT_TYPES.BUTTON}_`)
	const callButtons = filter(buttons, (button: any) => button?.value?.formItemGeneral.action === BUTTON_ACTIONS.CALL_NUMBER)
	const priorities: string[] = []

	forEach(callButtons, (btn) => {
		priorities.push(get(btn, 'value.formItemGeneral.priority', ''))
	})

	const createRefForTabBox = (key: string, layoutHeight: number) => {
		setTabBoxLayoutReferences({
			...tabBoxLayoutReferences,
			[key]: { height: layoutHeight }
		})
	}

	// calculate tab box layout height
	const calculateTabBoxLayoutHeight = (i: string) => {
		const doc: any = document
		// default height of tab box layout
		let result = DEFAULT_TAB_BOX_HEIGHT
		if (doc) {
			// get tabBox height based on tab box id
			const tabBoxHeight = doc.getElementById(`${i}`)?.style.height.match(/[+-]?\d+(\.\d+)?/g)[0]
			if (tabBoxHeight) {
				// subtraction of nav tabs bar height
				result = parseFloat(tabBoxHeight) - TAB_BOX_TABS_NAV_WRAP_HEIGHT
			}
		}
		return result
	}

	// create map of heights for tab boxes
	const initRefForTabBoxes = (formItems: IElementItem, level: number): any => {
		// get all tab boxes elements
		const tabBoxes = Object.keys(formItems).filter((key: string) => formItems[`${key}`]?.type === ELEMENT_TYPES.TAB_BOX)
		let ref: any = {}
		tabBoxes.forEach((key) => {
			if (level === 0) {
				formItems[key].tabs.forEach((tab: ITabsElement) => {
					// call initRefForTabBoxes fun for nested tab box
					ref = { ...ref, [`${key}`]: { ...ref[`${key}`], [`${tab.key}`]: { ...initRefForTabBoxes(tab.data.items, level + 1) } } }
				})
			}
			ref = { ...ref, [`${key}`]: { ...ref[`${key}`], height: calculateTabBoxLayoutHeight(key) } }
		})
		return ref
	}

	// change order of options based on pattern array
	const changeOptionsOrder = (options: any, pattern: ITabOption[]): ITabsElement[] => {
		const result: any[] = []
		pattern?.forEach((sort) => {
			let found = false
			// TODO - check if si working (prev options = options.filter)
			options.filter((option: ITabsElement) => {
				if (!found && option?.key === sort?.key) {
					result.push(option)
					found = true
					return false
				}
				return true
			})
		})
		return result
	}

	// parse data to structure using recursion calling
	const parseNestedData = (array: any[], parent: any, type?: string): IParsedData => {
		const formCopy: any[] = [...array]
		let result: any = {}
		if (type === ELEMENT_TYPES.TAB) {
			result = []
		} else {
			result = { items: {}, layout: [] }
		}
		array.forEach((item: any, index) => {
			try {
				if (array[index]?.parent === parent) {
					if (array[index]?.type === ELEMENT_TYPES.TAB_BOX) {
						// parsed data for tab box based on tab box key
						const tabs = parseNestedData(array, array[index]?.i, ELEMENT_TYPES.TAB)
						const options = JSON.parse(array[index]?.formItemGeneral)?.options
						// change tabs order
						formCopy[index].tabs = changeOptionsOrder(tabs, options)
					} else if (array[index]?.type === ELEMENT_TYPES.TAB) {
						result = [
							...result,
							{
								key: formCopy[index].i,
								title: formCopy[index].title,
								id: formCopy[index].id,
								// parsed data for tab based on tab key
								data: parseNestedData(array, array[index]?.i)
							}
						]
					}
					if (array[index]?.type !== ELEMENT_TYPES.TAB) {
						result = {
							items: {
								...result.items,
								[`${formCopy[index].i}`]: {
									id: formCopy[index].id,
									type: formCopy[index].type,
									formItemGeneral: JSON.parse(formCopy[index]?.formItemGeneral),
									formItemCSS: JSON.parse(formCopy[index]?.formItemCSS),
									tabs: formCopy[index]?.tabs
								}
							},
							layout: [
								...result.layout,
								{
									x: parseInt(formCopy[index].x, 10),
									y: parseInt(formCopy[index].y, 10),
									w: parseInt(formCopy[index].w, 10),
									h: parseInt(formCopy[index].h, 10),
									i: formCopy[index].i,
									static: formCopy[index].static
								}
							]
						}
					}
				}
			} catch (err: any) {
				// eslint-disable-next-line no-console
				console.error(err)
			}
		})
		return result
	}

	// parse loaded data
	const parseData = async () => {
		dispatch(getColumns(templateId, page))
		dispatch({ type: RESET_FORM_STORE })
		let formTemplate: any = {}
		let form: any = {}
		let formDb = ''
		// load templates or forms based on site path
		setSpinner(true)
		if (page === PAGE.TEMPLATE_BUILDER) {
			formTemplate = await dispatch(getTemplate(templateId))
			form.formItems = formTemplate?.data?.templateItems
			// init document tab for selected template
			dispatch(initialize(FORM.DOCUMENT_FORM, formTemplate.data?.formDocument))
			formDb = formTemplate.data?.formDatabase
		} else {
			formTemplate = await dispatch(getFormTemplate(templateId))
			form = formTemplate?.data?.backup
			// init document tab for selected form
			dispatch(initialize(FORM.DOCUMENT_FORM, formTemplate?.data?.backup?.document))
			formDb = formTemplate?.data?.database
		}
		// load form items to table in document tab
		dispatch(getFormTableItems(templateId, page))
		setDatabase(formDb)
		const parsedData: IParsedData = parseNestedData(form?.formItems || [], null)
		setLayout([...parsedData.layout])
		setItems({ ...parsedData.items })
		// call resize event on window to update layout items position
		window.dispatchEvent(new Event('resize'))
		setSpinner(false)
	}

	// filter elements in left side bar elements list
	const filterElementsList = (value: string) => {
		const searchedText = value.toLowerCase().split(' ')
		const result = elementsList.filter((item) => searchedText.every((el) => item.label.toLowerCase().indexOf(el) > -1))
		if (result.length > 0) {
			setElements(result)
		} else {
			// check filtered result and if found nothing then show all elements
			setElements([...elementsList])
		}
	}

	// return form key based on element type
	const initForms = (type: string) => {
		switch (type) {
			case ELEMENT_TYPES.INPUT:
				return FORM.INPUT_ELEMENT_FORM
			case ELEMENT_TYPES.BUTTON:
				return FORM.BUTTON_ELEMENT_FORM
			case ELEMENT_TYPES.TEXT:
				return FORM.PARAGRAPH_ELEMENT_FORM
			case ELEMENT_TYPES.TITLE:
				return FORM.TITLE_ELEMENT_FORM
			case ELEMENT_TYPES.TEXT_AREA:
				return FORM.TEXT_AREA_ELEMENT_FORM
			case ELEMENT_TYPES.HORIZONTAL_LINE:
				return FORM.HORIZONTAL_LINE_ELEMENT_FORM
			case ELEMENT_TYPES.DATE_PICKER:
				return FORM.DATE_ELEMENT_FORM
			case ELEMENT_TYPES.DROPDOWN_BUTTON:
				return FORM.DROPDOWNBUTTON_ELEMENT_FORM
			case ELEMENT_TYPES.CHECK_BOX:
				return FORM.CHECKBOX_ELEMENT_FORM
			case ELEMENT_TYPES.RADIO_BUTTON:
				return FORM.RADIO_BUTTON_ELEMENT_FORM
			case ELEMENT_TYPES.IMAGE:
				return FORM.IMAGE_ELEMENT_FORM
			case ELEMENT_TYPES.TABLE:
				return FORM.TABLE_ELEMENT_FORM
			case ELEMENT_TYPES.TAB_BOX:
				return FORM.TAB_BOX_ELEMENT_FORM
			case ELEMENT_TYPES.SELECT_FROM_TABLE:
				return FORM.SELECT_FROM_TABLE_ELEMENT_FORM
			default:
				return ''
		}
	}

	// parse tabs from tab box to format for redux form array
	const parseTabsToItemProps = (tabsArray: ITabsElement[]): ITabOption[] => {
		const options: ITabOption[] = []
		tabsArray?.forEach((tab) => {
			options.push({ title: tab.title, key: tab.key, id: tab.id })
		})
		return options
	}

	// parse conditions and add key 'type' to result
	const parseConditions = (type: string, data: any, condition: string): any[] => {
		const result = partition(data?.[ELEMENT_ATTRIBUTES.CONDITIONS]?.[type], { type: condition })
		if (result[0]?.length > 0) {
			return result[0]
		}
		return []
	}

	// initialize validation form with conditions for dynamic validation and visibility
	const initializeValidationForm = (selectedElement: IFormElement) => {
		// only for horizontal line is not allowed use this function
		if (selectedElement?.type !== ELEMENT_TYPES.HORIZONTAL_LINE) {
			// parse conditions for validation modal
			if (ELEMENTS_WITH_VISIBILITY.includes(selectedElement.type)) {
				const visibilityAnd = parseConditions(ELEMENT_ATTRIBUTES.VISIBILITY, selectedElement?.formItemGeneral, 'AND') || []
				const visibilityOr = parseConditions(ELEMENT_ATTRIBUTES.VISIBILITY, selectedElement?.formItemGeneral, 'OR') || []
				// initialize validation form only for visibility function
				dispatch(
					initialize(FORM.VALIDATION_FORM, {
						[VALIDATION_FORM_FIELD_ARRAYS.VISIBILITY_AND_OPTIONS]: [...visibilityAnd, ...addEmptyRow(visibilityAnd)],
						[VALIDATION_FORM_FIELD_ARRAYS.VISIBILITY_OR_OPTIONS]: [...visibilityOr, ...addEmptyRow(visibilityOr)]
					})
				)
			}
			if (ELEMENTS_WITH_VISIBILITY_AND_VALIDATION.includes(selectedElement.type)) {
				const visibilityAnd = parseConditions(ELEMENT_ATTRIBUTES.VISIBILITY, selectedElement?.formItemGeneral, 'AND') || []
				const visibilityOr = parseConditions(ELEMENT_ATTRIBUTES.VISIBILITY, selectedElement?.formItemGeneral, 'OR') || []
				const validationAnd = parseConditions(ELEMENT_ATTRIBUTES.VALIDATION, selectedElement?.formItemGeneral, 'AND') || []
				const validationOr = parseConditions(ELEMENT_ATTRIBUTES.VALIDATION, selectedElement?.formItemGeneral, 'OR') || []
				// initialize validation form only for both validation and visibility function
				dispatch(
					initialize(FORM.VALIDATION_FORM, {
						[VALIDATION_FORM_FIELD_ARRAYS.REQUIRED_AND_OPTIONS]: [...validationAnd, ...addEmptyRow(validationAnd)],
						[VALIDATION_FORM_FIELD_ARRAYS.REQUIRED_OR_OPTIONS]: [...validationOr, ...addEmptyRow(validationOr)],
						[VALIDATION_FORM_FIELD_ARRAYS.VISIBILITY_AND_OPTIONS]: [...visibilityAnd, ...addEmptyRow(visibilityAnd)],
						[VALIDATION_FORM_FIELD_ARRAYS.VISIBILITY_OR_OPTIONS]: [...visibilityOr, ...addEmptyRow(visibilityOr)]
					})
				)
			}
		}
	}

	// edit element item properties
	const openItem = (item: IGridItem) => {
		// check for callback from tab box if is static true then ignore
		if (!item.static) {
			if (selectedTab !== '' && selectedTabItem !== '') {
				setSelectedTab('')
				setSelectedTabItem('')
				setSelectedNestedTabItem('')
				setSelectedNestedTab('')
			}
			// check if is no same as previous selected element item
			if (item.i !== selectedItem || items[item.i]?.type === ELEMENT_TYPES.TAB_BOX) {
				if (
					items[item.i]?.type === ELEMENT_TYPES.TABLE ||
					items[item.i]?.type === ELEMENT_TYPES.BUTTON ||
					items[item.i]?.type === ELEMENT_TYPES.INPUT
				) {
					dispatch(getProcedures(database))
				}
				if (items[item.i]?.type === ELEMENT_TYPES.BUTTON) {
					dispatch(getFormTabs(templateId, page))
					dispatch(getEmailsConfigs())
				}
				const formData: any = { ...items[item.i]?.formItemGeneral }
				if (items[item.i]?.type === ELEMENT_TYPES.TAB_BOX) {
					// if tab box parse tabs to format for redux form array
					formData.options = parseTabsToItemProps(items[item.i]?.tabs)
				}
				dispatch(getElements(templateId, page))
				// parse and set init state of validation modal conditions
				initializeValidationForm(items[item.i])
				// init element attributes form
				dispatch(
					initialize(initForms(items[item.i]?.type), {
						...formData,
						i: item.i
					})
				)
				// init element CSS form
				dispatch(
					initialize(FORM.ELEMENT_CSS_FORM, {
						css: items[item.i]?.formItemCSS?.css,
						inheritClasses: items[item.i]?.formItemCSS?.inheritClasses || []
					})
				)
				setSelectedItem(item.i)
				setSelectedType(items[item.i]?.type)
				// set active tab key in right element properties sidebar
				setActiveElPropertiesTabKey(ITEM_PROPERTIES_RIGHT_SIDEBAR_TAB_KEYS.TAB1)
			}
		}
	}

	// if something change in grid layout synchronize component state
	const onLayoutChange = async (formLayout: IGridItem[]) => {
		// check if is layout empty
		if (!isEmpty(formLayout)) {
			setLayout([...formLayout.filter((item) => item.i !== DROPPING_ELEMENT.KEY)])
		}
	}

	// remove element from grid layout
	const onRemoveItem = async (i: string) => {
		try {
			dispatch({ type: LOAD, load: true })
			await deleteReq(getApiEndpointPath(page || '', templateId, items[`${i}`].id), undefined)
			/* eslint-disable-next-line @typescript-eslint/no-unused-vars */
			const { [i]: item, ...updatedItems } = items
			setActiveElPropertiesTabKey(ITEM_PROPERTIES_RIGHT_SIDEBAR_TAB_KEYS.TAB3)
			setItems({ ...updatedItems })
			setLayout(layout.filter((gridItem) => gridItem.i !== i))
			const edited = true
			dispatch({ type: FORM_EDITED, edited })
			// load form items to table in document tab
			dispatch(getFormTableItems(templateId, page))
		} catch (err: any) {
			// if some error occurred when user add new element, reload whole form items
			parseData()
			// eslint-disable-next-line no-console
			console.error(err.message)
		} finally {
			dispatch({ type: LOAD, load: false })
		}
	}

	// create tab for new created tab box element
	const createTabForNewTabBox = async (formItem: IFormItemData): Promise<ITabsElement[]> => {
		try {
			dispatch({ type: LOAD, load: true })
			const tabs: ITabsElement[] = []
			let newTab: any = {}
			newTab = {
				title: 'Tab',
				i: uuidv4(),
				type: ELEMENT_TYPES.TAB,
				parent: formItem.i
			}
			// create new tab in tab box
			const res = await postReq(getApiEndpointPath(page || '', templateId), undefined, newTab)
			tabs.push({
				title: newTab.title,
				key: newTab.i,
				id: res.data?.data?.formItemID,
				data: {
					items: {},
					layout: []
				}
			})
			return await Promise.resolve([...tabs])
		} catch (err: any) {
			// if some error occurred when user add new element, reload whole form items
			parseData()
			// eslint-disable-next-line no-console
			console.error(err.message)
			return await Promise.reject()
		} finally {
			dispatch({ type: LOAD, load: false })
		}
	}

	// add new element item to grid layout and update state layouts and items
	const onNewItemAdded = async (
		droppedItemData: IListItem | any,
		layoutItem: IGridItem,
		itemKey?: string,
		activeTabKey?: string,
		parentItemKey?: string,
		parentActiveTabKey?: string,
		fetchData?: boolean,
		defaultValues?: any
	) => {
		if (droppedItemData.key === OBJECTS.OBJECT_STATUS) {
			if (layoutItem) {
				// eslint-disable-next-line @typescript-eslint/no-use-before-define
				addNewObjectItems(droppedItemData.key, layoutItem, itemKey, activeTabKey, parentItemKey, parentActiveTabKey)
			}
			return
		}
		if (!layoutItem) {
			return
		}
		let key = ''
		let height = 0
		let item: IFormElement = {
			id: '',
			type: '',
			formItemGeneral: {}
		}
		// check if create new item from form item copy
		if (droppedItemData?.formItemGeneral && droppedItemData?.type !== ELEMENT_TYPES.TAB_BOX) {
			height = droppedItemData.formItemGeneral.row / LAYOUT_ROW_HEIGHT
			key = `${droppedItemData.type}_${uuidv4()}`
			item = {
				id: '',
				type: droppedItemData?.type,
				formItemGeneral: { ...droppedItemData?.formItemGeneral }
			}
		} else {
			height = layoutItem.h
			key = `${droppedItemData.key}_${uuidv4()}`
			item = {
				id: '',
				type: droppedItemData.key,
				formItemGeneral: {}
			}
			// initial setup for specify element
			if (droppedItemData.key === ELEMENT_TYPES.INPUT) {
				item.formItemGeneral = {
					label: droppedItemData.label,
					row: height * LAYOUT_ROW_HEIGHT,
					size: FIELD_SIZE.M,
					labelPosition: ITEM_LABEL_POSITION.TOP,
					labelColor: DEFAULT_LABEL_COLOR,
					hintColor: DEFAULT_HINT_COLOR,
					placeholderColor: DEFAULT_PLACEHOLDER_COLOR,
					col: layoutItem.w,
					conditions: {
						validation: [],
						visibility: []
					},
					readOnly: false,
					required: false
				}
			} else if (droppedItemData.key === ELEMENT_TYPES.TEXT_AREA) {
				item.formItemGeneral = {
					label: droppedItemData.label,
					row: height * LAYOUT_ROW_HEIGHT,
					labelPosition: ITEM_LABEL_POSITION.TOP,
					labelColor: DEFAULT_LABEL_COLOR,
					hintColor: DEFAULT_HINT_COLOR,
					placeholderColor: DEFAULT_PLACEHOLDER_COLOR,
					col: layoutItem.w,
					conditions: {
						validation: [],
						visibility: []
					},
					readOnly: false,
					required: false
				}
			} else if (droppedItemData.key === ELEMENT_TYPES.BUTTON) {
				item.formItemGeneral = {
					label: droppedItemData.label,
					row: height * LAYOUT_ROW_HEIGHT,
					size: FIELD_SIZE.M,
					col: layoutItem.w,
					conditions: {
						visibility: []
					}
				}
			} else if (droppedItemData.key === ELEMENT_TYPES.TEXT) {
				item.formItemGeneral = {
					row: height * LAYOUT_ROW_HEIGHT,
					col: layoutItem.w,
					label: droppedItemData.label,
					labelColor: DEFAULT_LABEL_COLOR,
					conditions: {
						visibility: []
					}
				}
			} else if (droppedItemData.key === ELEMENT_TYPES.TITLE) {
				item.formItemGeneral = {
					row: height * LAYOUT_ROW_HEIGHT,
					col: layoutItem.w,
					label: droppedItemData.label,
					labelColor: DEFAULT_LABEL_COLOR,
					titleSize: DEFAULT_TITLE_SIZE,
					conditions: {
						visibility: []
					}
				}
			} else if (droppedItemData.key === ELEMENT_TYPES.HORIZONTAL_LINE) {
				item.formItemGeneral = {
					row: height * LAYOUT_ROW_HEIGHT,
					col: layoutItem.w,
					labelColor: DEFAULT_LABEL_COLOR
				}
			} else if (droppedItemData.key === ELEMENT_TYPES.DROPDOWN_BUTTON) {
				item.formItemGeneral = {
					label: droppedItemData.label,
					row: height * LAYOUT_ROW_HEIGHT,
					size: FIELD_SIZE.M,
					labelPosition: ITEM_LABEL_POSITION.TOP,
					hintColor: DEFAULT_HINT_COLOR,
					labelColor: DEFAULT_LABEL_COLOR,
					col: layoutItem.w,
					displayedOptions: DEFAULT_DISPLAYED_OPTIONS,
					conditions: {
						validation: [],
						visibility: []
					},
					readOnly: false,
					required: false
				}
			} else if (droppedItemData.key === ELEMENT_TYPES.DATE_PICKER) {
				item.formItemGeneral = {
					label: droppedItemData.label,
					row: height * LAYOUT_ROW_HEIGHT,
					labelColor: DEFAULT_LABEL_COLOR,
					labelPosition: ITEM_LABEL_POSITION.TOP,
					hintColor: DEFAULT_HINT_COLOR,
					size: FIELD_SIZE.M,
					col: layoutItem.w,
					date: true,
					conditions: {
						validation: [],
						visibility: []
					},
					readOnly: false,
					required: false
				}
			} else if (droppedItemData.key === ELEMENT_TYPES.CHECK_BOX) {
				item.formItemGeneral = {
					label: droppedItemData.label,
					row: height * LAYOUT_ROW_HEIGHT,
					labelPosition: ITEM_LABEL_POSITION.TOP,
					labelColor: DEFAULT_LABEL_COLOR,
					col: layoutItem.w,
					radioGroupAlign: RADIO_BUTTON_GROUP_ALIGNMENT.VERTICAL,
					conditions: {
						validation: [],
						visibility: []
					},
					readOnly: false,
					required: false
				}
			} else if (droppedItemData.key === ELEMENT_TYPES.RADIO_BUTTON) {
				item.formItemGeneral = {
					label: droppedItemData.label,
					row: height * LAYOUT_ROW_HEIGHT,
					labelPosition: ITEM_LABEL_POSITION.TOP,
					labelColor: DEFAULT_LABEL_COLOR,
					col: layoutItem.w,
					radioGroupAlign: RADIO_BUTTON_GROUP_ALIGNMENT.VERTICAL,
					conditions: {
						validation: [],
						visibility: []
					},
					readOnly: false,
					required: false
				}
			} else if (droppedItemData.key === ELEMENT_TYPES.IMAGE) {
				item.formItemGeneral = {
					row: height * LAYOUT_ROW_HEIGHT,
					col: layoutItem.w,
					conditions: {
						visibility: []
					}
				}
			} else if (droppedItemData.key === ELEMENT_TYPES.TABLE) {
				item.formItemGeneral = {
					label: droppedItemData.label,
					row: height * LAYOUT_ROW_HEIGHT,
					labelPosition: ITEM_LABEL_POSITION.TOP,
					labelColor: DEFAULT_LABEL_COLOR,
					col: layoutItem.w,
					conditions: {
						visibility: []
					},
					readOnly: false,
					required: false
				}
			} else if (droppedItemData.key === ELEMENT_TYPES.TAB_BOX) {
				item.formItemGeneral = {
					row: height * LAYOUT_ROW_HEIGHT,
					col: layoutItem.w,
					switchTabs: true,
					options: [],
					conditions: {
						visibility: []
					}
				}
			} else if (droppedItemData.key === ELEMENT_TYPES.SELECT_FROM_TABLE) {
				item.formItemGeneral = {
					label: droppedItemData.label,
					row: height * LAYOUT_ROW_HEIGHT,
					labelPosition: ITEM_LABEL_POSITION.TOP,
					labelColor: DEFAULT_LABEL_COLOR,
					col: layoutItem.w,
					maxSelectedRows: 0,
					conditions: {
						visibility: []
					}
				}
			} else {
				item.formItemGeneral = {
					label: droppedItemData.label,
					row: height * LAYOUT_ROW_HEIGHT,
					size: FIELD_SIZE.M,
					labelPosition: ITEM_LABEL_POSITION.TOP,
					labelColor: DEFAULT_LABEL_COLOR,
					col: layoutItem.w
				}
			}
		}

		// add default values to form item general
		if (defaultValues && !isEmpty(Object.keys(defaultValues))) {
			item.formItemGeneral = {
				...item.formItemGeneral,
				...defaultValues
			}
		}

		// creating new element for tab box on specify tab
		if (itemKey && activeTabKey) {
			// condition to prevent dropping tab box to nested tab box (not more levels then 1)
			if (parentItemKey && parentActiveTabKey && droppedItemData.key === ELEMENT_TYPES.TAB_BOX) {
				return
			}
			try {
				// check for fetch data on local data model
				if (!fetchData) {
					dispatch({ type: LOAD, load: true })
				}
				const formItem: IFormItemData = {
					x: layoutItem.x,
					y: layoutItem.y,
					i: key,
					h: height,
					w: layoutItem.w,
					type: droppedItemData.key || droppedItemData.type,
					static: false,
					formItemCSS: {},
					formItemGeneral: item.formItemGeneral,
					parent: activeTabKey
				}
				const response = await postReq(getApiEndpointPath(page || '', templateId), undefined, formItem)
				if (page === PAGE.TEMPLATE_BUILDER) {
					item.id = response.data?.data?.templateItemID
				} else {
					item.id = response.data?.data?.formItemID
				}
				if (droppedItemData.key === ELEMENT_TYPES.TAB_BOX) {
					item.tabs = await createTabForNewTabBox(formItem)
					item.formItemGeneral = { ...item.formItemGeneral, options: parseTabsToItemProps(item.tabs) }
					// it's not necessary to wait for a response when it crashes
					// the data is reloaded from the backend
					patchReq(getApiEndpointPath(page || '', templateId, item.id), undefined, {
						i: formItem.i,
						formItemGeneral: { ...item.formItemGeneral }
					})
				}
				// check for fetch data on local data model
				if (!fetchData) {
					let newItems: any = {}
					const newLayoutItem: IGridItem = {
						x: layoutItem.x,
						y: layoutItem.y,
						w: layoutItem.w,
						h: height,
						i: key,
						static: false
					}
					if (parentItemKey && parentActiveTabKey) {
						// add new element in nested tab box element
						const parentIndex = items?.[parentItemKey]?.tabs?.findIndex((tab: IElementItem) => tab.key === parentActiveTabKey)
						const clonedItems = { ...items }
						const childIndex = [...get(clonedItems, `${parentItemKey}.tabs[${parentIndex}].data.items[${itemKey}].tabs]`)].findIndex(
							(tab: IElementItem) => tab.key === activeTabKey
						)
						const secondLevelTabLayout = filter(
							get(clonedItems, `${parentItemKey}.tabs[${parentIndex}].data.items[${itemKey}].tabs[${childIndex}].data.layout`),
							(girdItem) => girdItem.i !== DROPPING_ELEMENT.KEY
						)
						newItems = set(clonedItems, `${parentItemKey}.tabs[${parentIndex}].data.items[${itemKey}].tabs[${childIndex}].data`, {
							items: {
								...get(clonedItems, `${parentItemKey}.tabs[${parentIndex}].data.items[${itemKey}].tabs[${childIndex}].data.items`),
								[`${key}`]: { ...item }
							},
							layout: [...secondLevelTabLayout, { ...newLayoutItem }]
						})
					} else {
						// add new element in tab box element
						const index = items?.[itemKey]?.tabs?.findIndex((tab: IElementItem) => tab.key === activeTabKey)
						const clonedItems = { ...items }
						const firstLevelTabLayout = filter(
							get(clonedItems, `${itemKey}.tabs[${index}].data.layout`),
							(girdItem) => girdItem.i !== DROPPING_ELEMENT.KEY
						)
						newItems = set(clonedItems, `${itemKey}.tabs[${index}].data`, {
							items: {
								...get(clonedItems, `${itemKey}.tabs[${index}].data.items`),
								[`${key}`]: { ...item }
							},
							layout: [...firstLevelTabLayout, { ...newLayoutItem }]
						})
					}
					setItems({ ...newItems })
					dispatch({ type: FORM_EDITED, edited: true })
					// load form items to table in document tab
					dispatch(getFormTableItems(templateId, page))
				}
			} catch (err: any) {
				// if some error occurred when user add new element, reload whole form items
				parseData()
				// eslint-disable-next-line no-console
				console.error(err.message)
			} finally {
				dispatch({ type: LOAD, load: false })
			}
		} else {
			// add grid item to layout
			const newLayout = [
				...layout,
				{
					x: layoutItem.x,
					y: layoutItem.y,
					w: layoutItem.w,
					i: key,
					h: height
				}
			]

			try {
				dispatch({ type: LOAD, load: true })
				const formItem: IFormItemData = {
					x: layoutItem.x,
					y: layoutItem.y,
					i: key,
					h: height,
					w: layoutItem.w,
					type: droppedItemData.key || droppedItemData.type,
					static: false,
					formItemCSS: {},
					formItemGeneral: item.formItemGeneral
				}
				const response = await postReq(getApiEndpointPath(page || '', templateId), undefined, formItem)
				if (page === PAGE.TEMPLATE_BUILDER) {
					item.id = response.data?.data?.templateItemID
				} else {
					item.id = response.data?.data?.formItemID
				}
				// if form element is tab box
				if (droppedItemData.key === ELEMENT_TYPES.TAB_BOX) {
					item.tabs = await createTabForNewTabBox(formItem)
					item.formItemGeneral = { ...item.formItemGeneral, options: parseTabsToItemProps(item.tabs) }
					// it's not necessary to wait for a response when it crashes
					// the data is reloaded from the backend
					patchReq(getApiEndpointPath(page || '', templateId, item.id), undefined, {
						i: formItem.i,
						formItemGeneral: { ...item.formItemGeneral }
					})
				}
				// check for fetch data on local data model
				if (!fetchData) {
					setItems({ ...items, [`${key}`]: { ...item } })
					setLayout(newLayout)
					createRefForTabBox(key, calculateTabBoxLayoutHeight(key))
					dispatch({ type: FORM_EDITED, edited: true })
					// load form items to table in document tab
					dispatch(getFormTableItems(templateId, page))
				}
			} catch (err: any) {
				// if some error occurred when user add new element, reload whole form items
				parseData()
				// eslint-disable-next-line no-console
				console.error(err.message)
			} finally {
				dispatch({ type: LOAD, load: false })
			}
		}
	}

	const addNewObjectItems = async (
		type: string,
		layoutItem: IGridItem,
		itemKey?: string,
		activeTabKey?: string,
		parentItemKey?: string,
		parentActiveTabKey?: string
	) => {
		if (type === OBJECTS.OBJECT_STATUS) {
			setSpinner(true)
			try {
				await Promise.all([
					// add date time picker
					onNewItemAdded(
						{ ...elementsList[7], label: STATUS_OBJECT_DEFAULT_VALUES.DATE_LABEL },
						{ ...layoutItem, w: 2 },
						itemKey,
						activeTabKey,
						parentItemKey,
						parentActiveTabKey,
						true,
						{
							[ELEMENT_ATTRIBUTES.TABLE_COLUMN]: 'request_call_date',
							[ELEMENT_ATTRIBUTES.TIME]: true
						}
					),
					// add dropdown button
					onNewItemAdded(
						{ ...elementsList[2], label: STATUS_OBJECT_DEFAULT_VALUES.DROPDOWN_LABEL },
						{
							...layoutItem,
							x: layoutItem.x + 2,
							w: 2
						},
						itemKey,
						activeTabKey,
						parentItemKey,
						parentActiveTabKey,
						true,
						{
							[ELEMENT_ATTRIBUTES.TABLE_COLUMN]: 'call_status_id',
							[ELEMENT_ATTRIBUTES.SQL]: 'SELECT daktela_status_name as name, daktela_status_id as value FROM def_status'
						}
					),
					// add button
					onNewItemAdded(
						{ ...elementsList[8], label: STATUS_OBJECT_DEFAULT_VALUES.BUTTON_LABEL },
						{
							...layoutItem,
							x: layoutItem.x + 4,
							w: 1
						},
						itemKey,
						activeTabKey,
						parentItemKey,
						parentActiveTabKey,
						true,
						{
							[ELEMENT_ATTRIBUTES.ACTION]: BUTTON_ACTIONS.SUBMIT_FORM,
							[ELEMENT_ATTRIBUTES.PREFIX_ICON]: 'save'
						}
					)
				])
			} catch (err: any) {
				// eslint-disable-next-line no-console
				console.error(err.message)
			} finally {
				setSpinner(false)
				parseData()
			}
		}
	}

	// lock or unlock tabs element - set attribute static with value
	const onTabsLocked = (key: string, value: boolean, parenElementKey?: string, parentTabKey?: string) => {
		// lock nested tab box element
		if (parenElementKey && parentTabKey) {
			const index = items[parenElementKey].tabs.findIndex((tab: ITabsElement) => tab.key === parentTabKey)
			const updateLayout = [...get(items, `${parenElementKey}.tabs[${index}].data.layout`)].map((grid) => {
				if (grid.i === key) {
					return { ...grid, static: value, isDraggable: !value }
				}
				return { ...grid }
			})
			const itemsCopy = { ...items }
			set(itemsCopy, `${parenElementKey}.tabs[${index}].data.layout`, updateLayout)
			setItems(itemsCopy)
		} else {
			// lock tab box element
			const updateLayout = layout.map((grid) => {
				if (grid.i === key) {
					return { ...grid, static: value, isDraggable: !value }
				}
				return { ...grid }
			})
			setLayout([...updateLayout])
		}
	}

	// add new tab to tab box element also for nested tab box element
	const onAddNewTab = async (tabBoxKey: string, tabKey: string, tabBoxParentKey?: string, tabBoxParentTabKey?: string, tabTitle?: string) => {
		try {
			dispatch({ type: LOAD, load: true })
			let data: any = {}
			const newTab = {
				title: tabTitle || 'New tab',
				i: tabKey,
				type: ELEMENT_TYPES.TAB,
				parent: tabBoxKey
			}
			const res = await postReq(getApiEndpointPath(page || '', templateId), undefined, newTab)
			const newTabId = res.data?.data?.formItemID
			// add tab for nested tab box element based on tabBoxParenKey and tabBoxParentTabKey
			if (tabBoxParentKey && tabBoxParentTabKey) {
				const tabsCopy = [...items[tabBoxParentKey].tabs]
				const index = tabsCopy.findIndex((tab) => tab.key === tabBoxParentTabKey)
				// add new added tab to options array in formItemGeneral
				set(items, `${tabBoxParentKey}.tabs[${index}].data.items[${tabBoxKey}].formItemGeneral`, {
					...get(items, `${tabBoxParentKey}.tabs[${index}].data.items[${tabBoxKey}].formItemGeneral`),
					options: [
						...get(items, `${tabBoxParentKey}.tabs[${index}].data.items[${tabBoxKey}].formItemGeneral.options`),
						{
							title: newTab.title,
							id: newTabId,
							key: newTab.i
						}
					]
				})
				const parentItem = get(items, `${tabBoxParentKey}.tabs[${index}].data.items[${tabBoxKey}]`)
				/**
				 * update parent tab box formItemGeneral options array
				 * it's not necessary to wait for a response when it
				 * crashes the data is reloaded from the backend
				 */
				patchReq(getApiEndpointPath(page || '', templateId, parentItem?.id), undefined, {
					i: parentItem?.i,
					formItemGeneral: parentItem?.formItemGeneral
				})
				data = { ...parentItem?.formItemGeneral }
				tabsCopy[index].data.items[`${tabBoxKey}`].tabs = [
					...tabsCopy[index].data.items[`${tabBoxKey}`].tabs,
					{
						title: newTab.title,
						key: newTab.i,
						id: newTabId,
						data: {
							items: {},
							layout: []
						}
					}
				]
				setItems((elementItems) => ({
					...elementItems,
					[tabBoxParentKey]: {
						...elementItems[tabBoxParentKey],
						tabs: [...tabsCopy]
					}
				}))
			} else {
				// add new added tab to options array in formItemGeneral
				set(items, `${tabBoxKey}.formItemGeneral`, {
					...get(items, `${tabBoxKey}.formItemGeneral`),
					options: [
						...get(items, `${tabBoxKey}.formItemGeneral.options`),
						{
							title: newTab.title,
							id: newTabId,
							key: newTab.i
						}
					]
				})
				const parentItem = items[`${tabBoxKey}`]
				/**
				 * update parent tab box formItemGeneral options array
				 * it's not necessary to wait for a response when it
				 * crashes the data is reloaded from the backend
				 */
				patchReq(getApiEndpointPath(page || '', templateId, parentItem?.id), undefined, {
					i: parentItem?.i,
					formItemGeneral: parentItem?.formItemGeneral
				})
				data = { ...parentItem?.formItemGeneral }
				// add tab for tab box element
				setItems((elementItems) => ({
					...elementItems,
					[tabBoxKey]: {
						...elementItems[tabBoxKey],
						tabs: [
							...elementItems[tabBoxKey].tabs,
							{
								title: newTab.title,
								key: newTab.i,
								id: newTabId,
								data: {
									items: {},
									layout: []
								}
							}
						]
					}
				}))
			}
			dispatch(initialize(initForms(selectedType), data))
		} catch (err: any) {
			// if some error occurred when user add new element, reload whole form items
			parseData()
			// eslint-disable-next-line no-console
			console.error(err.message)
		} finally {
			dispatch({ type: LOAD, load: false })
		}
	}

	// remove tab from tab box element also for nested tab box element
	const onRemoveTab = async (tabBoxKey: string, tabKey: string, tabBoxParentKey?: string, tabBoxParentTabKey?: string) => {
		let removedTab: ITabsElement[] = []
		let type = ''
		let data: any = {}
		try {
			dispatch({ type: LOAD, load: true })
			// remove tab for nested tab box element based on tabBoxParenKey and tabBoxParentTabKey
			if (tabBoxParentKey && tabBoxParentTabKey) {
				type = selectedType
				// finding parent tab index
				const index = items[tabBoxParentKey]?.tabs.findIndex((tab: ITabsElement) => tab.key === tabBoxParentTabKey)
				removedTab = items[tabBoxParentKey].tabs[index].data.items[tabBoxKey].tabs.filter((tab: ITabsElement) => tab.key === tabKey)
				await deleteReq(getApiEndpointPath(page || '', templateId, removedTab?.[0]?.id), undefined)
				const itemsCopy = { ...items }
				// copy of nested tab box element tabs without removed tab
				const tabsCopy = items[tabBoxParentKey].tabs[index].data.items[tabBoxKey].tabs.filter((tab: ITabsElement) => tab.key !== tabKey)
				// prepare data for form
				const tabsOptions = parseTabsToItemProps([...tabsCopy])
				data = {
					...itemsCopy[tabBoxParentKey].tabs[index].data.items[tabBoxKey]?.formItemGeneral,
					options: tabsOptions
				}
				// update formItemGeneral tabs option for tab box element
				set(items, `${tabBoxParentKey}.tabs[${index}].data.items[${tabBoxKey}].formItemGeneral`, {
					...get(items, `${tabBoxParentKey}.tabs[${index}].data.items[${tabBoxKey}].formItemGeneral`),
					options: [
						...get(items, `${tabBoxParentKey}.tabs[${index}].data.items[${tabBoxKey}].formItemGeneral.options`).filter(
							(option: ITabOption) => option.key !== tabKey
						)
					]
				})
				const parentItem = get(items, `${tabBoxParentKey}.tabs[${index}].data.items[${tabBoxKey}]`)
				/**
				 * update parent tab box formItemGeneral options array
				 * it's not necessary to wait for a response when it
				 * crashes the data is reloaded from the backend
				 */
				patchReq(getApiEndpointPath(page || '', templateId, parentItem?.id), undefined, {
					i: parentItem?.i,
					formItemGeneral: { ...parentItem?.formItemGeneral, options: tabsOptions }
				})
				// remove tab in nested tab box
				itemsCopy[tabBoxParentKey].tabs[index].data.items[tabBoxKey].tabs = [...tabsCopy]
				setItems({ ...itemsCopy })
			} else {
				type = items[tabBoxKey]?.type
				// remove tab for tab box element
				// finding tab
				removedTab = items[tabBoxKey].tabs.filter((tab: any) => tab.key === tabKey)
				await deleteReq(getApiEndpointPath(page || '', templateId, removedTab?.[0]?.id), undefined)
				// prepare data for form
				const tabsOptions = parseTabsToItemProps([...items[tabBoxKey].tabs.filter((tab: any) => tab.key !== tabKey)])
				data = {
					...items[tabBoxKey]?.formItemGeneral,
					options: tabsOptions
				}
				// update formItemGeneral tabs option for tab box element
				set(items, `${tabBoxKey}.formItemGeneral`, {
					...get(items, `${tabBoxKey}.formItemGeneral`),
					options: [...get(items, `${tabBoxKey}.formItemGeneral.options`).filter((option: ITabOption) => option.key !== tabKey)]
				})
				const parentItem = items[`${tabBoxKey}`]
				/**
				 * update parent tab box formItemGeneral options array
				 * it's not necessary to wait for a response when it
				 * crashes the data is reloaded from the backend
				 */
				patchReq(getApiEndpointPath(page || '', templateId, parentItem?.id), undefined, {
					i: parentItem?.i,
					formItemGeneral: { ...parentItem?.formItemGeneral, options: tabsOptions }
				})
				setItems((elementItems) => ({
					...elementItems,
					[tabBoxKey]: {
						...elementItems[tabBoxKey],
						tabs: [...elementItems[tabBoxKey].tabs.filter((tab: any) => tab.key !== tabKey)]
					}
				}))
				const edited = true
				dispatch({ type: FORM_EDITED, edited })
			}
			// update form (attribute right sideBar) for tab box element
			dispatch(initialize(initForms(type), data))
		} catch (err: any) {
			// if some error occurred when user add new element, reload whole form items
			parseData()
			// eslint-disable-next-line no-console
			console.error(err.message)
		} finally {
			dispatch({ type: LOAD, load: false })
		}
	}

	// open item and load attributes to right side bar form
	const openItemInTabsElement = (gridItem: IGridItem, itemKey: string, activeKey: string, tabBoxParentKey?: string, tabBoxParentTabKey?: string) => {
		// check for callback from nested tab box if is static true then ignore
		if (!gridItem.static) {
			let selectedElement: any = {}
			if (activeKey && tabBoxParentKey) {
				// get selected element from nested tab box element
				const parentIndex = items?.[tabBoxParentKey]?.tabs?.findIndex((tab: IElementItem) => tab.key === tabBoxParentTabKey)
				const childIndex = get(items, `${tabBoxParentKey}.tabs[${parentIndex}].data.items[${itemKey}].tabs`)?.findIndex(
					(tab: IElementItem) => tab.key === activeKey
				)
				selectedElement = {
					...get(items, `${tabBoxParentKey}.tabs[${parentIndex}].data.items[${itemKey}].tabs[${childIndex}].data.items[${gridItem.i}]`)
				}
			} else {
				// get selected element from tab box element
				const index = items?.[itemKey]?.tabs?.findIndex((tab: IElementItem) => tab.key === activeKey)
				selectedElement = { ...get(items, `${itemKey}.tabs[${index}].data.items[${gridItem.i}]`) }
			}
			// check if is same as previous selected element item
			if (gridItem.i !== selectedTabItem || selectedElement?.type === ELEMENT_TYPES.TAB_BOX) {
				let data: any = {}
				// check if is nested tab box element and prepare data for form
				if (selectedElement?.type === ELEMENT_TYPES.TAB_BOX) {
					data = {
						...selectedElement?.formItemGeneral,
						options: parseTabsToItemProps(selectedElement?.tabs)
					}
				} else {
					data = {
						...selectedElement?.formItemGeneral
					}
				}
				dispatch(getElements(templateId, page))
				if (
					selectedElement?.type === ELEMENT_TYPES.TABLE ||
					selectedElement?.type === ELEMENT_TYPES.BUTTON ||
					selectedElement?.type === ELEMENT_TYPES.INPUT
				) {
					dispatch(getProcedures(database))
				}
				if (selectedElement?.type === ELEMENT_TYPES.BUTTON) {
					dispatch(getEmailsConfigs())
					dispatch(getFormTabs(templateId, page))
				}
				// init element attributes form
				dispatch(
					initialize(initForms(selectedElement?.type), {
						...data,
						i: selectedElement?.id
					})
				)
				// init element CSS form
				dispatch(
					initialize(FORM.ELEMENT_CSS_FORM, {
						css: selectedElement?.formItemCSS?.css,
						inheritClasses: selectedElement?.formItemCSS?.inheritClasses || []
					})
				)
				// parse and set init state of validation modal conditions
				initializeValidationForm(selectedElement)
				setSelectedType(selectedElement?.type)
				setSelectedItemIndex(selectedElement?.id)
				if (tabBoxParentKey && tabBoxParentTabKey) {
					// set parent component key for nested tab box
					setSelectedItem(tabBoxParentKey || '')
					setSelectedTab(tabBoxParentTabKey || '')
					setSelectedTabItem(itemKey)
					setSelectedNestedTab(activeKey)
					setSelectedNestedTabItem(gridItem.i)
				} else {
					setSelectedItem(itemKey)
					setSelectedTab(activeKey)
					setSelectedTabItem(gridItem.i)
					// clear selected item for nested tab box
					setSelectedNestedTab('')
					setSelectedNestedTabItem('')
				}
				// set active tab key in right element properties sidebar
				setActiveElPropertiesTabKey(ITEM_PROPERTIES_RIGHT_SIDEBAR_TAB_KEYS.TAB1)
			}
		}
	}

	// on tabs element layout change
	// eslint-disable-next-line @typescript-eslint/no-shadow
	const onLayoutChangeInTabsElement = (itemKey: string, activeTabKey: string, layout: IGridItem[], tabBoxParenKey?: string, tabBoxParentTabKey?: string) => {
		if (tabBoxParenKey && tabBoxParentTabKey) {
			// update form item in nested tab box element
			const index = items?.[tabBoxParenKey]?.tabs?.findIndex((tab: IElementItem) => tab.key === tabBoxParentTabKey)
			const nestedIndex = items[tabBoxParenKey]?.tabs?.[index]?.data?.items?.[itemKey]?.tabs?.findIndex((tab: IElementItem) => tab.key === activeTabKey)
			const clonedItems = { ...items }
			// clone nested tab in nested tab box element
			const clonedNestedTab = { ...get(clonedItems, `${tabBoxParenKey}.tabs[${index}].data.items[${itemKey}].tabs[${nestedIndex}].data`) }
			// update data attribute of nested tab in nested tab box element
			const updatedNestedTabData = set(clonedNestedTab, 'layout', [...layout])
			const newItems = set(clonedItems, `${tabBoxParenKey}.tabs[${index}].data.items[${itemKey}].tabs[${nestedIndex}].data`, {
				...updatedNestedTabData
			})
			setItems({ ...newItems })
		} else {
			// update form item in tab box element
			const index = items[itemKey].tabs?.findIndex((tab: IElementItem) => tab.key === activeTabKey)
			const clonedItems = { ...items }
			const newItems = set(clonedItems, `${itemKey}.tabs[${index}].data`, {
				items: {
					...get(clonedItems, `${itemKey}.tabs[${index}].data.items`)
				},
				layout: [...layout]
			})
			setItems({ ...newItems })
		}
	}

	// remove element from tabs element
	const onRemoveItemInTabsElement = async (
		itemId: string,
		itemKey: string,
		elementKey: string,
		activeTabKey: string,
		tabBoxParenKey?: string,
		tabBoxParentTabKey?: string
	) => {
		try {
			dispatch({ type: LOAD, load: true })
			let newItems: any = {}
			const clonedItems = { ...items }
			await deleteReq(getApiEndpointPath(page || '', templateId, itemId), undefined)
			if (tabBoxParenKey && tabBoxParentTabKey) {
				// remove elements in nested tab box element
				const parentIndex = items?.[tabBoxParenKey]?.tabs?.findIndex((tab: IElementItem) => tab.key === tabBoxParentTabKey)
				const childIndex = get(items, `${tabBoxParenKey}.tabs[${parentIndex}].data.items[${elementKey}].tabs`)?.findIndex(
					(tab: IElementItem) => tab.key === activeTabKey
				)
				newItems = set(clonedItems, `${tabBoxParenKey}.tabs[${parentIndex}].data.items[${elementKey}].tabs[${childIndex}].data`, {
					items: {
						...omit(get(clonedItems, `${tabBoxParenKey}.tabs[${parentIndex}].data.items[${elementKey}].tabs[${childIndex}].data.items`), itemKey)
					},
					layout: [
						...get(clonedItems, `${tabBoxParenKey}.tabs[${parentIndex}].data.items[${elementKey}].tabs[${childIndex}].data.layout`).filter(
							(gridItem: IGridItem) => gridItem.i !== itemKey
						)
					]
				})
			} else {
				// remove elements in tab box element
				const index = items?.[elementKey]?.tabs?.findIndex((tab: IElementItem) => tab.key === activeTabKey)
				newItems = set(clonedItems, `${elementKey}.tabs[${index}].data`, {
					items: {
						...omit(get(clonedItems, `${elementKey}.tabs[${index}].data.items`), itemKey)
					},
					layout: [...get(clonedItems, `${elementKey}.tabs[${index}].data.layout`).filter((gridItem: IGridItem) => gridItem.i !== itemKey)]
				})
			}
			setItems({ ...newItems })
			setActiveElPropertiesTabKey(ITEM_PROPERTIES_RIGHT_SIDEBAR_TAB_KEYS.TAB3)
			// load form items to table in document tab
			dispatch(getFormTableItems(templateId, page))
			const edited = true
			dispatch({ type: FORM_EDITED, edited })
		} catch (err: any) {
			// if some error occurred when user add new element, reload whole form items
			parseData()
			// eslint-disable-next-line no-console
			console.error(err.message)
		} finally {
			dispatch({ type: LOAD, load: false })
		}
	}

	const generateRefForTabBoxes = (key: string) => {
		if (items[`${key}`]?.type === ELEMENT_TYPES.TAB_BOX) {
			createRefForTabBox(key, calculateTabBoxLayoutHeight(key))
		}
	}

	// get element size based on height
	const getSizeOfElement = (type: string, height: number): string => {
		let size = ''
		if (type === ELEMENT_TYPES.INPUT) {
			if (height === NUMBER_OF_ROWS.S_SIZE * LAYOUT_ROW_HEIGHT) {
				size = FIELD_SIZE.S
			} else if (height === NUMBER_OF_ROWS.M_SIZE * LAYOUT_ROW_HEIGHT) {
				size = FIELD_SIZE.M
			} else if (height === NUMBER_OF_ROWS.L_SIZE * LAYOUT_ROW_HEIGHT) {
				size = FIELD_SIZE.L
			}
		} else if (type === ELEMENT_TYPES.BUTTON) {
			if (height === NUMBER_OF_ROWS.BUTTON_S_SIZE * LAYOUT_ROW_HEIGHT) {
				size = FIELD_SIZE.S
			} else if (height === NUMBER_OF_ROWS.BUTTON_M_SIZE * LAYOUT_ROW_HEIGHT) {
				size = FIELD_SIZE.M
			}
		} else if (type === ELEMENT_TYPES.DATE_PICKER) {
			if (height === NUMBER_OF_ROWS.S_SIZE * LAYOUT_ROW_HEIGHT) {
				size = FIELD_SIZE.S
			} else if (height === NUMBER_OF_ROWS.M_SIZE * LAYOUT_ROW_HEIGHT) {
				size = FIELD_SIZE.M
			} else if (height === NUMBER_OF_ROWS.L_SIZE * LAYOUT_ROW_HEIGHT) {
				size = FIELD_SIZE.L
			}
		} else if (type === ELEMENT_TYPES.DROPDOWN_BUTTON) {
			if (height === NUMBER_OF_ROWS.S_SIZE * LAYOUT_ROW_HEIGHT) {
				size = FIELD_SIZE.S
			} else if (height === NUMBER_OF_ROWS.M_SIZE * LAYOUT_ROW_HEIGHT) {
				size = FIELD_SIZE.M
			} else if (height === NUMBER_OF_ROWS.L_SIZE * LAYOUT_ROW_HEIGHT) {
				size = FIELD_SIZE.L
			}
		} else if (type === ELEMENT_TYPES.SELECT_FROM_TABLE) {
			if (height === NUMBER_OF_ROWS.S_SIZE * LAYOUT_ROW_HEIGHT) {
				size = FIELD_SIZE.S
			} else if (height === NUMBER_OF_ROWS.M_SIZE * LAYOUT_ROW_HEIGHT) {
				size = FIELD_SIZE.M
			} else if (height === NUMBER_OF_ROWS.L_SIZE * LAYOUT_ROW_HEIGHT) {
				size = FIELD_SIZE.L
			}
		}
		return size
	}

	/*
	 	call onWindowResize when user change element width - only for Tabs element
	 	(child component TabsElementsBuilder)
	 */
	const resizeElement = (
		formLayout: IGridItem[],
		gridItem: IGridItem,
		itemKey?: string,
		activeKey?: string,
		tabBoxParentKey?: string,
		tabBoxParentTabKey?: string
	) => {
		let updatedFormItemGeneral: any = {}
		const updatedHeight = gridItem.h * LAYOUT_ROW_HEIGHT
		let selectedFormItem: IFormElement = {
			id: '',
			type: '',
			formItemGeneral: {}
		}
		let type = ''
		let size = ''
		if (itemKey && activeKey) {
			if (tabBoxParentKey && tabBoxParentTabKey && tabBoxParentKey !== itemKey && tabBoxParentTabKey !== activeKey) {
				// update items when resize elements in nested tab box
				const parentIndex = items?.[itemKey]?.tabs?.findIndex((tab: IElementItem) => tab.key === activeKey)
				const childIndex = get(items, `${itemKey}.tabs[${parentIndex}].data.items[${tabBoxParentKey}]`)?.tabs?.findIndex(
					(tab: IElementItem) => tab.key === tabBoxParentTabKey
				)
				selectedFormItem = get(items, `${itemKey}.tabs[${parentIndex}].data.items[${tabBoxParentKey}].tabs[${childIndex}].data.items[${gridItem.i}]`)
				updatedFormItemGeneral = { ...(selectedFormItem?.formItemGeneral || {}) }
				size = getSizeOfElement(selectedFormItem?.type, updatedHeight)
				updatedFormItemGeneral.size = size
				updatedFormItemGeneral.row = updatedHeight
				updatedFormItemGeneral.col = gridItem.w
				const itemsCopy = { ...items }
				const tabsCopy = [...get(items, `${itemKey}.tabs[${parentIndex}].data.items[${tabBoxParentKey}].tabs`)]
				set(tabsCopy, `[${childIndex}].data.items[${gridItem.i}].formItemGeneral`, updatedFormItemGeneral)
				set(itemsCopy, `${itemKey}.tabs[${parentIndex}].data.items[${tabBoxParentKey}].tabs`, tabsCopy)
				setItems({ ...itemsCopy })
			} else {
				// update items when resize elements in tab box
				const index = items?.[itemKey]?.tabs?.findIndex((tab: IElementItem) => tab.key === activeKey)
				selectedFormItem = get(items, `${itemKey}.tabs[${index}].data.items[${gridItem.i}]`)
				updatedFormItemGeneral = { ...(selectedFormItem?.formItemGeneral || {}) }
				size = getSizeOfElement(selectedFormItem?.type, updatedHeight)
				updatedFormItemGeneral.size = size
				updatedFormItemGeneral.row = updatedHeight
				updatedFormItemGeneral.col = gridItem.w
				const tabsCopy = [...get(items, `${itemKey}.tabs`)]
				const updatedItems = { ...tabsCopy[index] }
				set(updatedItems, `data.items[${gridItem?.i}].formItemGeneral`, updatedFormItemGeneral)
				setItems({
					...items,
					[itemKey]: {
						...items[itemKey],
						tabs: [...tabsCopy]
					}
				})
				if (selectedType === ELEMENT_TYPES.TAB_BOX) {
					// update options for form right sidebar for nested tab box
					updatedFormItemGeneral.options = parseTabsToItemProps(get(items, `${itemKey}.tabs[${index}].data.items[${gridItem.i}].tabs`))
				}
			}
			type = selectedFormItem?.type
		} else {
			// update items when resize elements in main builder layout
			type = items[`${gridItem.i}`]?.type
			updatedFormItemGeneral = { ...(items[gridItem.i]?.formItemGeneral || {}) }
			size = getSizeOfElement(type, updatedHeight)
			updatedFormItemGeneral.size = size
			updatedFormItemGeneral.row = updatedHeight
			updatedFormItemGeneral.col = gridItem.w
			setItems({ ...items, [gridItem.i]: { ...items[gridItem.i], formItemGeneral: { ...updatedFormItemGeneral } } })
			if (selectedType === ELEMENT_TYPES.TAB_BOX) {
				// update options for form right sidebar for tab box
				updatedFormItemGeneral.options = parseTabsToItemProps(items[`${gridItem.i}`].tabs)
			}
		}
		// update form in right sidebar
		dispatch(initialize(initForms(type), updatedFormItemGeneral))
		if (type === ELEMENT_TYPES.TAB_BOX) {
			// call onWindowResize when user change tab
			window.dispatchEvent(new Event('resize'))
		}
	}

	// update affected items based on resizing of item
	const updateRelocatedItems = (updatedItem: IGridItem, formLayout: IGridItem[]) => {
		let elementId = ''
		let parentIndex = 0
		let index = 0
		if (selectedTabItem !== '') {
			if (selectedNestedTab !== '') {
				parentIndex = get(items, `${selectedItem}.tabs`)?.findIndex((tab: ITabsElement) => tab.key === selectedTab)
				index = get(items, `${selectedItem}.tabs[${parentIndex}].data.items[${selectedTabItem}].tabs`)?.findIndex(
					(tab: ITabsElement) => tab.key === selectedNestedTab
				)
			} else {
				index = items?.[selectedItem]?.tabs?.findIndex((tab: ITabsElement) => tab.key === selectedTab)
			}
		}
		formLayout.forEach((item) => {
			if (updatedItem.i !== '' && item.y >= updatedItem.y + updatedItem.h) {
				try {
					const formItem: IFormItemData = {
						i: item.i,
						y: item.y
					}
					// check if is updating elements in tab box element
					if (selectedTabItem !== '') {
						if (selectedNestedTab !== '') {
							// for elements in nested tab box element
							elementId = get(
								items,
								`${selectedItem}.tabs[${parentIndex}].data.items[${selectedTabItem}].tabs[${index}].data.items[${item.i}]`
							)?.id
						} else {
							// for elements in tab box element
							elementId = get(items, `${selectedItem}.tabs[${index}].data.items[${item.i}]`)?.id
						}
					} else {
						// for elements in main layout
						elementId = items[item.i].id
					}
					patchReq(getApiEndpointPath(page || '', templateId, elementId), undefined, formItem)
				} catch (err: any) {
					// if some error occurred when user updating position of element, reload whole form items
					parseData()
					// eslint-disable-next-line no-console
					console.error(err.message)
				}
			}
		})
	}

	// resize element height based on label position, hint and size
	const changeHeightOrWidthOfElement = (i: string, height: number, width: number, formLayout: IGridItem[]): IGridItem[] => {
		let updatedItem: IGridItem = {
			x: 0,
			y: 0,
			w: 0,
			h: 0,
			i: ''
		}
		let heightChanged = true
		const newLayout: IGridItem[] = formLayout.map((item) => {
			if (item.i === i) {
				updatedItem = {
					x: item.x,
					y: item.y,
					w: width,
					h: height,
					i: item.i,
					static: item.static
				}
				// check item height change
				if (item?.h === height) {
					heightChanged = false
				}
				return updatedItem
			}
			return { ...item }
		})
		// if item height change then update neighbor items
		// which is hitted by height change
		if (heightChanged) {
			updateRelocatedItems(updatedItem, newLayout)
		}
		return newLayout
	}

	// parse tabs data to items format from redux form array format
	const parseOptionsToTabs = async (options: ITabOption[]) => {
		let tabsCopy: ITabsElement[] = [...items[selectedItem].tabs]
		let parentKey: string = selectedItem
		dispatch({ type: LOAD, load: true })
		// get copy for nested tab box element
		if (selectedTabItem && selectedTabItem !== '' && selectedType === ELEMENT_TYPES.TAB_BOX) {
			parentKey = selectedTabItem
			const index = items[selectedItem]?.tabs?.findIndex((tab: ITabsElement) => tab.key === selectedTab)
			tabsCopy = [...get(items, `${selectedItem}.tabs[${index}].data.items[${selectedTabItem}].tabs`)]
		}
		// when new tab is added from properties right side bar
		if (options.length > tabsCopy.length) {
			const newTab = {
				title: 'New tab',
				i: uuidv4(),
				type: ELEMENT_TYPES.TAB,
				parent: parentKey
			}
			const res = await postReq(getApiEndpointPath(page || '', templateId), undefined, newTab)
			tabsCopy.push({
				title: newTab.title,
				key: newTab.i,
				id: res.data?.data?.formItemID,
				data: {
					items: {},
					layout: []
				}
			})
		} else if (options.length > 0) {
			// when tabs title is updated from properties right side bar
			const updatedTabs = options.filter((option, index: number) => option.key === tabsCopy[index].key && option.title !== tabsCopy[index].title)
			await Promise.all(
				updatedTabs.map(async (tab) => {
					if (tab.title !== '') {
						await patchReq(getApiEndpointPath(page || '', templateId, tab.id), undefined, {
							title: tab.title
						})
						const index = tabsCopy?.findIndex((itemTab: ITabsElement) => itemTab.id === tab.id)
						tabsCopy[index].title = tab.title
					}
				})
			)
		}
		return tabsCopy
	}

	// remove unnecessary attributes from form item general for button form element
	// based on action
	const removeUnnecessaryAttributesForButtonActions = (formItemGeneral: any) => {
		let result: any = { ...formItemGeneral }
		if (formItemGeneral?.[ELEMENT_ATTRIBUTES.ACTION] === BUTTON_ACTIONS.SUBMIT_FORM) {
			result = {
				...omit(formItemGeneral, [
					BUTTON_ACTIONS_ATTRIBUTES.EMAIL_SUBJECT,
					BUTTON_ACTIONS_ATTRIBUTES.EMAIL_BODY,
					BUTTON_ACTIONS_ATTRIBUTES.FORM_ITEM_ID,
					BUTTON_ACTIONS_ATTRIBUTES.URL,
					BUTTON_ACTIONS_ATTRIBUTES.PROCEDURE,
					BUTTON_ACTIONS_ATTRIBUTES.FIELDS_LIST,
					BUTTON_ACTIONS_ATTRIBUTES.TAB,
					BUTTON_ACTIONS_ATTRIBUTES.SMS_BODY,
					BUTTON_ACTIONS_ATTRIBUTES.EMAIL_CONFIG,
					BUTTON_ACTIONS_ATTRIBUTES.URL_TYPE,
					BUTTON_ACTIONS_ATTRIBUTES.QUERY_ATTRIBUTES,
					BUTTON_ACTIONS_ATTRIBUTES.OPEN_MODAL,
					BUTTON_ACTIONS_ATTRIBUTES.MODAL_SIZE
				])
			}
		} else if (formItemGeneral?.[ELEMENT_ATTRIBUTES.ACTION] === BUTTON_ACTIONS.CALL_NUMBER) {
			result = {
				...omit(formItemGeneral, [
					BUTTON_ACTIONS_ATTRIBUTES.EMAIL_SUBJECT,
					BUTTON_ACTIONS_ATTRIBUTES.EMAIL_BODY,
					BUTTON_ACTIONS_ATTRIBUTES.URL,
					BUTTON_ACTIONS_ATTRIBUTES.PROCEDURE,
					BUTTON_ACTIONS_ATTRIBUTES.FIELDS_LIST,
					BUTTON_ACTIONS_ATTRIBUTES.TAB,
					BUTTON_ACTIONS_ATTRIBUTES.SMS_BODY,
					BUTTON_ACTIONS_ATTRIBUTES.EMAIL_CONFIG,
					BUTTON_ACTIONS_ATTRIBUTES.URL_TYPE,
					BUTTON_ACTIONS_ATTRIBUTES.QUERY_ATTRIBUTES,
					BUTTON_ACTIONS_ATTRIBUTES.OPEN_MODAL,
					BUTTON_ACTIONS_ATTRIBUTES.MODAL_SIZE
				])
			}
		} else if (formItemGeneral?.[ELEMENT_ATTRIBUTES.ACTION] === BUTTON_ACTIONS.SEND_SMS) {
			result = {
				...omit(formItemGeneral, [
					BUTTON_ACTIONS_ATTRIBUTES.EMAIL_SUBJECT,
					BUTTON_ACTIONS_ATTRIBUTES.EMAIL_BODY,
					BUTTON_ACTIONS_ATTRIBUTES.URL,
					BUTTON_ACTIONS_ATTRIBUTES.PROCEDURE,
					BUTTON_ACTIONS_ATTRIBUTES.FIELDS_LIST,
					BUTTON_ACTIONS_ATTRIBUTES.TAB,
					BUTTON_ACTIONS_ATTRIBUTES.EMAIL_CONFIG,
					BUTTON_ACTIONS_ATTRIBUTES.URL_TYPE,
					BUTTON_ACTIONS_ATTRIBUTES.QUERY_ATTRIBUTES,
					BUTTON_ACTIONS_ATTRIBUTES.OPEN_MODAL,
					BUTTON_ACTIONS_ATTRIBUTES.MODAL_SIZE
				])
			}
		} else if (formItemGeneral?.[ELEMENT_ATTRIBUTES.ACTION] === BUTTON_ACTIONS.SEND_EMAIL) {
			result = {
				...omit(formItemGeneral, [
					BUTTON_ACTIONS_ATTRIBUTES.URL,
					BUTTON_ACTIONS_ATTRIBUTES.PROCEDURE,
					BUTTON_ACTIONS_ATTRIBUTES.FIELDS_LIST,
					BUTTON_ACTIONS_ATTRIBUTES.TAB,
					BUTTON_ACTIONS_ATTRIBUTES.SMS_BODY,
					BUTTON_ACTIONS_ATTRIBUTES.URL_TYPE,
					BUTTON_ACTIONS_ATTRIBUTES.QUERY_ATTRIBUTES,
					BUTTON_ACTIONS_ATTRIBUTES.OPEN_MODAL,
					BUTTON_ACTIONS_ATTRIBUTES.MODAL_SIZE
				])
			}
		} else if (formItemGeneral?.[ELEMENT_ATTRIBUTES.ACTION] === BUTTON_ACTIONS.CALL_PROCEDURE) {
			result = {
				...omit(formItemGeneral, [
					BUTTON_ACTIONS_ATTRIBUTES.EMAIL_SUBJECT,
					BUTTON_ACTIONS_ATTRIBUTES.EMAIL_BODY,
					BUTTON_ACTIONS_ATTRIBUTES.FORM_ITEM_ID,
					BUTTON_ACTIONS_ATTRIBUTES.URL,
					BUTTON_ACTIONS_ATTRIBUTES.TAB,
					BUTTON_ACTIONS_ATTRIBUTES.SMS_BODY,
					BUTTON_ACTIONS_ATTRIBUTES.EMAIL_CONFIG,
					BUTTON_ACTIONS_ATTRIBUTES.URL_TYPE,
					BUTTON_ACTIONS_ATTRIBUTES.QUERY_ATTRIBUTES,
					BUTTON_ACTIONS_ATTRIBUTES.OPEN_MODAL,
					BUTTON_ACTIONS_ATTRIBUTES.MODAL_SIZE
				])
			}
		} else if (formItemGeneral?.[ELEMENT_ATTRIBUTES.ACTION] === BUTTON_ACTIONS.CALL_URL) {
			result = {
				...omit(formItemGeneral, [
					BUTTON_ACTIONS_ATTRIBUTES.EMAIL_SUBJECT,
					BUTTON_ACTIONS_ATTRIBUTES.EMAIL_BODY,
					BUTTON_ACTIONS_ATTRIBUTES.FORM_ITEM_ID,
					BUTTON_ACTIONS_ATTRIBUTES.PROCEDURE,
					BUTTON_ACTIONS_ATTRIBUTES.FIELDS_LIST,
					BUTTON_ACTIONS_ATTRIBUTES.TAB,
					BUTTON_ACTIONS_ATTRIBUTES.SMS_BODY,
					BUTTON_ACTIONS_ATTRIBUTES.EMAIL_CONFIG
				])
			}
		} else if (formItemGeneral?.[ELEMENT_ATTRIBUTES.ACTION] === BUTTON_ACTIONS.GO_TO_NEXT_TAB) {
			result = {
				...omit(formItemGeneral, [
					BUTTON_ACTIONS_ATTRIBUTES.EMAIL_SUBJECT,
					BUTTON_ACTIONS_ATTRIBUTES.EMAIL_BODY,
					BUTTON_ACTIONS_ATTRIBUTES.FORM_ITEM_ID,
					BUTTON_ACTIONS_ATTRIBUTES.URL,
					BUTTON_ACTIONS_ATTRIBUTES.PROCEDURE,
					BUTTON_ACTIONS_ATTRIBUTES.FIELDS_LIST,
					BUTTON_ACTIONS_ATTRIBUTES.SMS_BODY,
					BUTTON_ACTIONS_ATTRIBUTES.EMAIL_CONFIG,
					BUTTON_ACTIONS_ATTRIBUTES.URL_TYPE,
					BUTTON_ACTIONS_ATTRIBUTES.QUERY_ATTRIBUTES,
					BUTTON_ACTIONS_ATTRIBUTES.OPEN_MODAL,
					BUTTON_ACTIONS_ATTRIBUTES.MODAL_SIZE
				])
			}
		}
		return result
	}

	// update element properties
	const handleElementFormSubmit = async (formData: any) => {
		dispatch({ type: LOAD, load: true })
		let updatedFormItemGeneral: any = {}
		let selectedElement: IFormItemData = {}
		let elementId = ''
		let index = 0
		let parentIndex = 0
		let isSizeEdited = true
		let parsedTabs: ITabsElement[] = []
		if (selectedTab !== '' && selectedTabItem !== '') {
			if (selectedNestedTab !== '' && selectedNestedTabItem !== '') {
				// get elements in nested tab box
				parentIndex = items?.[selectedItem]?.tabs?.findIndex((tab: IElementItem) => tab.key === selectedTab)
				index = [...get(items, `${selectedItem}.tabs[${parentIndex}].data.items[${selectedTabItem}].tabs`)]?.findIndex(
					(tab: IElementItem) => tab.key === selectedNestedTab
				)
				selectedElement = {
					...get(items, `${selectedItem}.tabs[${parentIndex}].data.items[${selectedTabItem}].tabs[${index}].data.items[${selectedNestedTabItem}]`)
				}
				updatedFormItemGeneral = { ...(selectedElement?.formItemGeneral || {}) }
				elementId = selectedElement?.id || ''
			} else {
				// get elements in tab box
				index = items?.[selectedItem]?.tabs?.findIndex((tab: IElementItem) => tab.key === selectedTab)
				selectedElement = { ...get(items, `${selectedItem}.tabs[${index}].data.items[${selectedTabItem}]`) }
				updatedFormItemGeneral = { ...(selectedElement?.formItemGeneral || {}) }
				elementId = selectedElement?.id || ''
			}
		} else {
			selectedElement = { ...items[selectedItem] }
			updatedFormItemGeneral = { ...(items[selectedItem]?.formItemGeneral || {}) }
			elementId = items?.[selectedItem]?.id
		}
		if (formData?.size === '' || !formData?.size) {
			isSizeEdited = false
		}
		// update data for selected element
		let heightCount = 0
		// eslint-disable-next-line no-restricted-syntax
		for (const key of Object.keys(formData)) {
			if (isSizeEdited) {
				if (key === ELEMENT_ATTRIBUTES.HINT) {
					if (formData[key] !== '' && formData[key] !== null) {
						heightCount += NUMBER_OF_ROWS.HINT_POSITION
					}
				} else if (key === ELEMENT_ATTRIBUTES.SIZE) {
					if (selectedType === ELEMENT_TYPES.BUTTON) {
						if (formData[key] === FIELD_SIZE.S) {
							heightCount += NUMBER_OF_ROWS.BUTTON_S_SIZE
						} else if (formData[key] === FIELD_SIZE.M) {
							heightCount += NUMBER_OF_ROWS.BUTTON_M_SIZE
						}
					} else if (selectedType === ELEMENT_TYPES.INPUT) {
						if (formData[key] === FIELD_SIZE.S) {
							heightCount += NUMBER_OF_ROWS.S_SIZE
						} else if (formData[key] === FIELD_SIZE.M) {
							heightCount += NUMBER_OF_ROWS.M_SIZE
						} else if (formData[key] === FIELD_SIZE.L) {
							heightCount += NUMBER_OF_ROWS.L_SIZE
						}
					} else if (selectedType === ELEMENT_TYPES.SELECT_FROM_TABLE) {
						if (formData[key] === FIELD_SIZE.S) {
							heightCount += NUMBER_OF_ROWS.S_SIZE
						} else if (formData[key] === FIELD_SIZE.M) {
							heightCount += NUMBER_OF_ROWS.M_SIZE
						} else if (formData[key] === FIELD_SIZE.L) {
							heightCount += NUMBER_OF_ROWS.L_SIZE
						}
					} else if (selectedType === ELEMENT_TYPES.DATE_PICKER) {
						if (formData[key] === FIELD_SIZE.S) {
							heightCount += NUMBER_OF_ROWS.S_SIZE
						} else if (formData[key] === FIELD_SIZE.M) {
							heightCount += NUMBER_OF_ROWS.M_SIZE
						} else if (formData[key] === FIELD_SIZE.L) {
							heightCount += NUMBER_OF_ROWS.L_SIZE
						}
					} else if (selectedType === ELEMENT_TYPES.DROPDOWN_BUTTON) {
						if (formData[key] === FIELD_SIZE.S) {
							heightCount += NUMBER_OF_ROWS.S_SIZE
						} else if (formData[key] === FIELD_SIZE.M) {
							heightCount += NUMBER_OF_ROWS.M_SIZE
						} else if (formData[key] === FIELD_SIZE.L) {
							heightCount += NUMBER_OF_ROWS.L_SIZE
						}
					}
				} else if (selectedType === ELEMENT_TYPES.HORIZONTAL_LINE) {
					heightCount = NUMBER_OF_ROWS.HORIZONTAL_LINE
				} else if (key === ELEMENT_ATTRIBUTES.LABEL_POSITION) {
					if (formData[key] === ITEM_LABEL_POSITION.TOP) {
						heightCount += NUMBER_OF_ROWS.LABEL_POSITION
					}
				} else if (key === ELEMENT_ATTRIBUTES.RADIO_GROUP_ALIGN) {
					if (formData[key] === RADIO_BUTTON_GROUP_ALIGNMENT.HORIZONTAL) {
						heightCount += NUMBER_OF_ROWS.LABEL_POSITION
					} else if (formData[key] === RADIO_BUTTON_GROUP_ALIGNMENT.VERTICAL) {
						heightCount += NUMBER_OF_ROWS.LABEL_POSITION
					}
				}
			} else if (updatedFormItemGeneral.row % LAYOUT_ROW_HEIGHT === 0) {
				// check the height of the element is a multiple of the base height of the line
				heightCount = updatedFormItemGeneral.row / LAYOUT_ROW_HEIGHT
			} else {
				// if not set zero
				heightCount = 0
			}
			if (key === FORM_KEYS.OPTIONS && selectedType === ELEMENT_TYPES.TAB_BOX) {
				try {
					// eslint-disable-next-line no-await-in-loop
					const parserTabsResult = await parseOptionsToTabs(formData[key])
					// check if added new item
					if ((selectedElement?.tabs?.length || 0) < formData[key]?.length) {
						// update id of new tab added
						set(formData[key], `[${parserTabsResult.length - 1}].id`, parserTabsResult[parserTabsResult.length - 1]?.id)
						set(formData[key], `[${parserTabsResult.length - 1}].key`, parserTabsResult[parserTabsResult.length - 1]?.key)
					}
					// change order of tabs based on tab options patter
					parsedTabs = changeOptionsOrder(parserTabsResult, formData[key])
					// update tab box element options
					updatedFormItemGeneral[key] = [...formData[key]]
				} catch (err: any) {
					// if some error occurred when user add new element, reload whole form items
					parseData()
					// eslint-disable-next-line no-console
					console.error(err.message)
				}
			} else if (key !== ELEMENT_ATTRIBUTES.COL || (key === ELEMENT_ATTRIBUTES.COL && formData[key] > 0 && formData[key] <= DEFAULT_NUMBERS_OF_COLS)) {
				updatedFormItemGeneral[key] = formData[key]
			}
		}

		try {
			let itemId: string = selectedItem
			// for selected element in tab box element
			if (selectedTab !== '' && selectedTabItem !== '') {
				if (selectedNestedTab !== '' && selectedNestedTabItem !== '') {
					itemId = selectedNestedTabItem
				} else {
					itemId = selectedTabItem
				}
			}
			const newWidth = parseInt(updatedFormItemGeneral?.col, 10)
			const prevStateOfSelectedItem = { ...selectedElement?.formItemGeneral }
			let heightHelp = 0
			// check if user change height form input
			if (updatedFormItemGeneral.row !== prevStateOfSelectedItem.row) {
				// check the height of the element is a multiple of the base height of the line
				if (updatedFormItemGeneral.row % LAYOUT_ROW_HEIGHT === 0 && updatedFormItemGeneral.row > 0) {
					heightHelp = updatedFormItemGeneral.row
					heightCount = heightHelp / LAYOUT_ROW_HEIGHT
					updatedFormItemGeneral = { ...updatedFormItemGeneral, size: '' }
				} else {
					// if not set previous value
					heightHelp = prevStateOfSelectedItem.row
				}
			} else {
				heightHelp = heightCount * LAYOUT_ROW_HEIGHT
			}

			// update row height in pixels
			updatedFormItemGeneral.row = heightHelp
			if (!(heightCount > 0)) {
				heightCount = heightHelp / LAYOUT_ROW_HEIGHT
			}
			// check button actions and remove unnecessary attributes
			if (updatedFormItemGeneral?.[ELEMENT_ATTRIBUTES.ACTION]) {
				updatedFormItemGeneral = removeUnnecessaryAttributesForButtonActions(updatedFormItemGeneral)
			}
			const formItem: IFormItemData = {
				i: itemId,
				h: heightCount,
				w: newWidth,
				formItemGeneral: updatedFormItemGeneral
			}
			await patchReq(getApiEndpointPath(page || '', templateId, elementId), undefined, formItem)
			if (selectedTab !== '' && selectedTabItem !== '') {
				if (selectedNestedTab !== '' && selectedNestedTabItem !== '') {
					// update elements attributes inside of nested tab box
					const tabsCopy = [...get(items, `${selectedItem}.tabs[${parentIndex}].data.items[${selectedTabItem}].tabs`)]
					set(tabsCopy, `${index}.data.items[${selectedNestedTabItem}].formItemGeneral`, updatedFormItemGeneral)
					// update layout based on element attributes (like size etc.)
					const updatedLayout = [
						...changeHeightOrWidthOfElement(selectedNestedTabItem, heightCount, newWidth, [...get(tabsCopy, `${index}.data.layout`)])
					]
					set(tabsCopy, `${index}.data.layout`, updatedLayout)
					const itemsCopy = { ...items }
					set(itemsCopy, `${selectedItem}.tabs[${parentIndex}].data.items[${selectedTabItem}].tabs`, tabsCopy)
					setItems({ ...itemsCopy })
				} else {
					// update elements inside of tab box
					const tabsCopy = [...items[selectedItem].tabs]
					const updatedItems = { ...tabsCopy[index] }
					if (selectedType === ELEMENT_TYPES.TAB_BOX) {
						if (parsedTabs && parsedTabs.length > 0) {
							// update tabs in nested tab box element from form in right sidebar
							const tabIndex = items?.[selectedItem]?.tabs?.findIndex((tab: ITabsElement) => tab.key === selectedTab)
							const itemsCopy = { ...items }
							set(itemsCopy, `${selectedItem}.tabs[${tabIndex}].data.items[${selectedTabItem}].tabs`, parsedTabs)
							set(itemsCopy, `${selectedItem}.tabs[${tabIndex}].data.items[${selectedTabItem}].formItemGeneral`, updatedFormItemGeneral)
							setItems({ ...itemsCopy })
							updatedFormItemGeneral.options = [...parseTabsToItemProps(parsedTabs)]
						}
					} else {
						// update elements in tabs inside tab box element
						updatedItems.data.items[selectedTabItem].formItemGeneral = updatedFormItemGeneral
					}
					// need update layout when user change width in form
					updatedItems.data.layout = [...changeHeightOrWidthOfElement(selectedTabItem, heightCount, newWidth, updatedItems.data.layout)]
					setItems({
						...items,
						[selectedItem]: {
							...items[selectedItem],
							tabs: [...tabsCopy]
						}
					})
				}
			} else {
				setLayout([...changeHeightOrWidthOfElement(selectedItem, heightCount, newWidth, layout)])
				if (parsedTabs && parsedTabs.length > 0) {
					// update tabs in tab box element
					setItems({
						...items,
						[selectedItem]: { ...items[selectedItem], formItemGeneral: updatedFormItemGeneral, tabs: [...parsedTabs] }
					})
					updatedFormItemGeneral.options = [...parseTabsToItemProps(parsedTabs)]
				} else {
					setItems({
						...items,
						[selectedItem]: { ...items[selectedItem], formItemGeneral: updatedFormItemGeneral }
					})
				}
			}
			dispatch(initialize(initForms(selectedType), updatedFormItemGeneral))
			// load form items to table in document tab
			dispatch(getFormTableItems(templateId, page))
			const edited = true
			dispatch({ type: FORM_EDITED, edited })
			if (selectedType === ELEMENT_TYPES.TAB_BOX) {
				window.dispatchEvent(new Event('resize'))
			}
		} catch (err: any) {
			// if some error occurred when user updating position of element, reload whole form items
			parseData()
			// eslint-disable-next-line no-console
			console.error(err.message)
		} finally {
			dispatch({ type: LOAD, load: false })
		}
	}

	// handle element CSS form tab submit
	const handleCSSFormSubmit = (formData: any) => {
		try {
			// form item in main form
			let updatedItem: string = selectedItem
			let updatedItemId: string = items?.[selectedItem]?.id
			let index = -1
			let parentIndex = -1
			if (selectedTab !== '' && selectedTabItem !== '') {
				if (selectedNestedTab !== '' && selectedNestedTabItem !== '') {
					// form item in nested tab box element
					// get index of parent tab
					parentIndex = items?.[selectedItem]?.tabs?.findIndex((tab: IElementItem) => tab.key === selectedTab)
					// get index of nested tab
					index = [...get(items, `${selectedItem}.tabs[${parentIndex}].data.items[${selectedTabItem}].tabs`)]?.findIndex(
						(tab: IElementItem) => tab.key === selectedNestedTab
					)
					updatedItem = selectedNestedTabItem
					updatedItemId = selectedItemId
				} else {
					// form item in tab box element
					// get index of nested tab
					index = items?.[selectedItem]?.tabs?.findIndex((tab: IElementItem) => tab.key === selectedTab)
					updatedItem = selectedTabItem
					updatedItemId = selectedItemId
				}
			}
			// create new formItemCSS object with updated values
			const updatedFormItemCSS = { css: formData?.css, inheritClasses: formData?.inheritClasses }
			const formItem: IFormItemData = {
				i: updatedItem,
				formItemCSS: updatedFormItemCSS
			}
			patchReq(getApiEndpointPath(page || '', templateId, updatedItemId), undefined, formItem)
			const edited = true
			dispatch({ type: FORM_EDITED, edited })
			if (index >= 0) {
				let itemsCopy: any = {}
				if (parentIndex >= 0) {
					// update state for form item  in nested tab box element
					itemsCopy = set(
						{ ...items },
						`${selectedItem}.tabs[${parentIndex}].data.items[${selectedTabItem}].tabs[${index}].data.items[${selectedNestedTabItem}].formItemCSS`,
						updatedFormItemCSS
					)
				} else {
					// update state for form item in tab box element
					itemsCopy = set({ ...items }, `${selectedItem}.tabs[${index}].data.items[${selectedTabItem}].formItemCSS`, updatedFormItemCSS)
				}
				setItems({ ...itemsCopy })
			} else {
				// update state for form item in main form
				setItems({ ...items, [updatedItem]: { ...items[updatedItem], formItemCSS: updatedFormItemCSS } })
			}
		} catch (err: any) {
			// if some error occurred when user updating position of element, reload whole form items
			parseData()
			// eslint-disable-next-line no-console
			console.error(err.message)
		}
	}

	const onTabAttributesElementClicked = (key: string) => {
		setActiveElPropertiesTabKey(key)
	}

	useEffect(() => {
		// load form data
		parseData()
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	// create references fo tab boxes
	useEffect(() => {
		// initial generation of references for tabBox element
		setTabBoxLayoutReferences(initRefForTabBoxes(items, 0))
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [items])

	const editClass = cx({
		'hide-element': !spinner
	})

	// remove separated item, remove nested item, remove nested item from tabbox functionality
	const handleRemoveItem = () => {
		if (selectedTab !== '' && selectedTabItem !== '') {
			if (selectedNestedTab !== '' && selectedNestedTabItem !== '') {
				onRemoveItemInTabsElement(selectedItemId, selectedNestedTabItem, selectedTabItem, selectedNestedTab, selectedItem, selectedTab)
			} else {
				onRemoveItemInTabsElement(selectedItemId, selectedTabItem, selectedItem, selectedTab)
			}
		} else {
			onRemoveItem(selectedItem)
		}
	}

	const createCopyOfFormItem = (itemKey: string, elementKey?: string, activeTabKey?: string, tabBoxParenKey?: string, tabBoxParentTabKey?: string) => {
		let copyItem: any = {}
		const clonedItems = { ...items }
		if (tabBoxParenKey && tabBoxParentTabKey) {
			// copy form item in nested tab box element
			const parentIndex = items?.[tabBoxParenKey]?.tabs?.findIndex((tab: IElementItem) => tab.key === tabBoxParentTabKey)
			const childIndex = get(items, `${tabBoxParenKey}.tabs[${parentIndex}].data.items[${elementKey}].tabs`)?.findIndex(
				(tab: IElementItem) => tab.key === activeTabKey
			)
			copyItem = get(clonedItems, `${tabBoxParenKey}.tabs[${parentIndex}].data.items[${elementKey}].tabs[${childIndex}].data.items[${itemKey}]`)
		} else if (activeTabKey && elementKey) {
			// copy form item in tab box element
			const index = items?.[elementKey]?.tabs?.findIndex((tab: IElementItem) => tab.key === activeTabKey)
			copyItem = get(clonedItems, `${elementKey}.tabs[${index}].data.items[${itemKey}]`)
		} else {
			// copy form item
			copyItem = get(clonedItems, `${itemKey}`)
		}
		dispatch(saveItemCopy(copyItem))
	}

	const handleCopyFormItem = () => {
		if (selectedTab !== '' && selectedTabItem !== '') {
			if (selectedNestedTab !== '' && selectedNestedTabItem !== '') {
				createCopyOfFormItem(selectedNestedTabItem, selectedTabItem, selectedNestedTab, selectedItem, selectedTab)
			} else {
				createCopyOfFormItem(selectedTabItem, selectedItem, selectedTab)
			}
		} else {
			createCopyOfFormItem(selectedItem)
		}
	}

	const onKeyDown = (keyName: string) => {
		// handle remove selected form item
		if (keyName === KEYS_SHORTCUTS.CTRL_D) {
			handleRemoveItem()
		}
		// handle copy selected form item
		if (keyName === KEYS_SHORTCUTS.CTRL_C) {
			handleCopyFormItem()
		}
	}

	return (
		<div className={'form-builder-page'}>
			<div className={`full-w-placeholder ${editClass}`}>
				<Spin size='large' />
			</div>
			<aside className={'items-left-sidebar'}>
				<ItemListLeftSideBar itemList={elements} filterList={filterElementsList} />
			</aside>
			<article className={'main-builder'}>
				{/* eslint-disable-next-line react/jsx-no-bind */}
				<Hotkeys keyName={`${KEYS_SHORTCUTS.CTRL_D},${KEYS_SHORTCUTS.CTRL_C}`} onKeyDown={onKeyDown.bind(this)}>
					<MainBuilder
						formId={templateId}
						layouts={layout}
						items={items}
						selectedItem={selectedItem}
						selectedTabItem={selectedTabItem}
						openItem={openItem}
						selectedNestedTabItem={selectedNestedTabItem}
						onLayoutChange={onLayoutChange}
						onNewItemAdded={onNewItemAdded}
						onClickSwitch={onTabsLocked}
						onAddNewTab={onAddNewTab}
						onRemoveTab={onRemoveTab}
						openItemInTabsElement={openItemInTabsElement}
						onLayoutChangeInTabsElement={onLayoutChangeInTabsElement}
						onNewItemAddedToTabsElement={onNewItemAdded}
						resizeElement={resizeElement}
						tabBoxLayoutReferences={tabBoxLayoutReferences}
						onRender={generateRefForTabBoxes}
						reload={parseData}
						page={page}
						nestedElementsRendered={() => {
							setTabBoxLayoutReferences(initRefForTabBoxes(items, 0))
						}}
					/>
				</Hotkeys>
			</article>
			<aside className={'items-properties-right-sidebar'} id={'items-properties-right-sidebar'}>
				<ItemPropertiesRightSideBar
					handleElementFormSubmit={handleElementFormSubmit}
					handleCSSFormSubmit={handleCSSFormSubmit}
					selectedItemType={selectedType}
					tabKey={activeElPropertiesTabKey}
					onTabClicked={onTabAttributesElementClicked}
					t={t}
					priorities={priorities}
					onRemoveItem={handleRemoveItem}
					formId={templateId}
					page={page}
				/>
			</aside>
		</div>
	)
}

export default React.memo(FormBuilderPage)
